import type { TFunction } from "i18next";

/**
 * Preset employee-status reasons. `code` is the STABLE value persisted in the DB — it is
 * decoupled from the translated label so renaming a label (or the admin's UI language) can
 * never orphan an existing row. `labelKey` is the i18n key used purely for display.
 * Free-text ("Other") reasons are persisted verbatim, outside this list.
 */
export const EMPLOYEE_STATUS_REASON_PRESETS = [
	{ code: "fmla", labelKey: "employee.status.reason.fmla" },
	{ code: "loa", labelKey: "employee.status.reason.loa" },
	{ code: "personal", labelKey: "employee.status.reason.personal" },
	{ code: "educational", labelKey: "employee.status.reason.educational" },
] as const;

export type EmployeeStatusReasonCode = (typeof EMPLOYEE_STATUS_REASON_PRESETS)[number]["code"];

const codeToLabelKey = new Map<string, string>(EMPLOYEE_STATUS_REASON_PRESETS.map((preset) => [preset.code, preset.labelKey]));

/**
 * Rows created before reasons were persisted as stable codes stored the translated label instead
 * (in whatever language the admin's UI was in). Map those known legacy strings back to their code
 * so they keep resolving to the right preset instead of falling through to "Other".
 */
const LEGACY_LABEL_TO_CODE = new Map<string, EmployeeStatusReasonCode>([
	["FMLA", "fmla"],
	["Family and medical leave", "fmla"],
	["Leave of Absence", "loa"],
	["Permiso de ausencia", "loa"],
	["חופשה ללא תשלום", "loa"],
]);

/** Resolve a persisted reason to a preset code, or null when it is free text / unset. */
export const reasonToPresetCode = (persistedReason: string | null): EmployeeStatusReasonCode | null => {
	if (!persistedReason) return null;
	if (codeToLabelKey.has(persistedReason)) return persistedReason as EmployeeStatusReasonCode;
	return LEGACY_LABEL_TO_CODE.get(persistedReason) ?? null;
};

/**
 * Human-readable reason for display (grid, tooltip, chip): preset codes (and known legacy labels)
 * become their translated label; free-text reasons pass through verbatim; null stays null.
 */
export const formatEmployeeStatusReason = (persistedReason: string | null, t: TFunction): string | null => {
	if (!persistedReason) return null;
	const code = reasonToPresetCode(persistedReason);
	if (!code) return persistedReason;
	const labelKey = codeToLabelKey.get(code);
	return labelKey ? t(labelKey) : persistedReason;
};
